import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import s from './JsonTree.module.css';

const INDENT_UNIT = '  ';

export type JsonSearchHighlight =
  | { mode: 'free'; needle: string }
  | { mode: 'pair'; key: string; value: string };

type JsonTreeProps = {
  data: unknown;
  searchHighlight: JsonSearchHighlight | null;
  matchPaths?: string[];
};

export type JsonTreeHandle = {
  expandAll: () => void;
  collapseAll: () => void;
};

function collectExpandablePaths(value: unknown, base = '', out: string[] = []): string[] {
  if (value === null || typeof value !== 'object') return out;

  if (base !== '') out.push(base);

  if (Array.isArray(value)) {
    value.forEach((v, i) => collectExpandablePaths(v, `${base}[${i}]`, out));
  } else {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      collectExpandablePaths(v, base ? `${base}.${k}` : k, out);
    }
  }

  return out;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function splitHighlight(text: string, needle: string, hitClass: string): ReactNode[] {
  const trimmed = needle.trim();
  if (!trimmed) return [text];
  let re: RegExp;
  try {
    re = new RegExp(escapeRegExp(trimmed), 'gi');
  } catch {
    return [text];
  }
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <mark key={`m${i}`} className={hitClass}>
        {match[0]}
      </mark>,
    );
    i += 1;
    lastIndex = re.lastIndex;
    if (match[0].length === 0) re.lastIndex += 1;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function highlightForKey(text: string, h: JsonSearchHighlight | null): ReactNode {
  if (!h) return text;
  if (h.mode === 'free') return splitHighlight(text, h.needle, s.searchHit);
  return splitHighlight(text, h.key, s.searchHitKey);
}

function highlightForValue(text: string, h: JsonSearchHighlight | null): ReactNode {
  if (!h) return text;
  if (h.mode === 'free') return splitHighlight(text, h.needle, s.searchHit);
  return splitHighlight(text, h.value, s.searchHitValue);
}

function ancestorPaths(path: string): string[] {
  if (!path) return [];
  const out: string[] = [];
  let acc = '';
  for (let i = 0; i < path.length; i += 1) {
    const c = path[i];
    if ((c === '.' || c === '[') && acc.length > 0) out.push(acc);
    acc += c;
  }
  out.push(acc);
  return out;
}

type Ctx = {
  expanded: Set<string>;
  forceExpanded: Set<string>;
  toggle: (path: string) => void;
  highlight: JsonSearchHighlight | null;
};

function PrimitiveValue({ value, h }: { value: unknown; h: JsonSearchHighlight | null }) {
  if (typeof value === 'string') {
    return <span className={s.str}>&quot;{highlightForValue(value, h)}&quot;</span>;
  }
  if (typeof value === 'number') {
    return <span className={s.num}>{highlightForValue(String(value), h)}</span>;
  }
  if (typeof value === 'boolean') {
    return <span className={s.kw}>{highlightForValue(String(value), h)}</span>;
  }
  if (value === null) {
    return <span className={s.kw}>{highlightForValue('null', h)}</span>;
  }
  return <span>{String(value)}</span>;
}

function Chevron({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={s.chevronBtn}
      onClick={onClick}
      aria-expanded={open}
      aria-label={open ? 'Collapse' : 'Expand'}>
      {open ? (
        <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
      ) : (
        <KeyboardArrowRightIcon sx={{ fontSize: 14 }} />
      )}
    </button>
  );
}

type NodeProps = {
  path: string;
  keyName: string | null;
  value: unknown;
  depth: number;
  isLast: boolean;
  ctx: Ctx;
};

function Node({ path, keyName, value, depth, isLast, ctx }: NodeProps): ReactNode {
  const indent = INDENT_UNIT.repeat(depth);
  const trailing = isLast ? '' : ',';
  const isRoot = path === '';

  const keyPart =
    keyName !== null ? (
      <>
        <span className={s.key}>&quot;{highlightForKey(keyName, ctx.highlight)}&quot;</span>
        <span className={s.punct}>: </span>
      </>
    ) : null;

  if (value === null || typeof value !== 'object') {
    return (
      <>
        {indent}
        {!isRoot && <span className={s.chevronSpacer} aria-hidden />}
        {keyPart}
        <PrimitiveValue value={value} h={ctx.highlight} />
        <span className={s.punct}>{trailing}</span>
        {'\n'}
      </>
    );
  }

  const isArray = Array.isArray(value);
  const open = isArray ? '[' : '{';
  const close = isArray ? ']' : '}';

  const entries = isArray
    ? (value as unknown[]).map((v, i) => ({
        keyName: null as string | null,
        indexLabel: `[${i}]`,
        val: v,
      }))
    : Object.entries(value as Record<string, unknown>).map(([k, v]) => ({
        keyName: k,
        indexLabel: null as string | null,
        val: v,
      }));

  if (entries.length === 0) {
    return (
      <>
        {indent}
        {!isRoot && <span className={s.chevronSpacer} aria-hidden />}
        {keyPart}
        <span className={s.punct}>
          {open}
          {close}
          {trailing}
        </span>
        {'\n'}
      </>
    );
  }

  const isExpanded = path === '' || ctx.expanded.has(path) || ctx.forceExpanded.has(path);
  const isCollapsed = !isExpanded;

  if (isCollapsed) {
    const itemLabel = isArray
      ? `${entries.length} ${entries.length === 1 ? 'item' : 'items'}`
      : `${entries.length} ${entries.length === 1 ? 'key' : 'keys'}`;
    return (
      <>
        {indent}
        <Chevron open={false} onClick={() => ctx.toggle(path)} />
        {keyPart}
        <span className={s.punct}>{open}</span>
        <span className={s.fold}> … {itemLabel} </span>
        <span className={s.punct}>
          {close}
          {trailing}
        </span>
        {'\n'}
      </>
    );
  }

  return (
    <>
      {indent}
      {!isRoot && <Chevron open onClick={() => ctx.toggle(path)} />}
      {keyPart}
      <span className={s.punct}>{open}</span>
      {'\n'}
      {entries.map((e, i) => {
        const childPath = e.indexLabel
          ? `${path}${e.indexLabel}`
          : path
            ? `${path}.${e.keyName}`
            : (e.keyName as string);
        return (
          <Node
            key={i}
            path={childPath}
            keyName={e.keyName}
            value={e.val}
            depth={depth + 1}
            isLast={i === entries.length - 1}
            ctx={ctx}
          />
        );
      })}
      {indent}
      <span className={s.punct}>
        {close}
        {trailing}
      </span>
      {'\n'}
    </>
  );
}

const JsonTree = forwardRef<JsonTreeHandle, JsonTreeProps>(function JsonTree(
  { data, searchHighlight, matchPaths },
  ref,
) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const forceExpanded = useMemo(() => {
    const out = new Set<string>();
    if (matchPaths?.length) {
      for (const p of matchPaths) {
        for (const a of ancestorPaths(p)) out.add(a);
      }
    }
    return out;
  }, [matchPaths]);

  const allExpandablePaths = useMemo(() => collectExpandablePaths(data), [data]);

  const toggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      expandAll: () => setExpanded(new Set(allExpandablePaths)),
      collapseAll: () => setExpanded(new Set()),
    }),
    [allExpandablePaths],
  );

  const ctx: Ctx = { expanded, forceExpanded, toggle, highlight: searchHighlight };

  return <Node path="" keyName={null} value={data} depth={0} isLast ctx={ctx} />;
});

export default JsonTree;
